// @core/stores/affiliate/AffiliateConversionStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { affiliateApi } from '@core/api';
import type {
  ApproveConversionRequest,
  Conversion,
  ConversionResponse,
  RejectConversionRequest,
} from '@core/types/affiliate';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class AffiliateConversionStore {
  // List state
  items: Conversion[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Conversion | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Conversion>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Conversion | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Conversion | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Approve conversion
   */
  async ApproveConversion(conversion_id: string, data: ApproveConversionRequest): Promise<ConversionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await affiliateApi.ApproveConversion(conversion_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get conversion by ID
   */
  async GetConversion(conversion_id: string): Promise<Conversion> {
    // Check cache first
    const cached = this.cache.get(conversion_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await affiliateApi.GetConversion(conversion_id);
      const item = response.conversion;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List conversions
   */
  async ListConversions(params?: PaginatedRequest & { affiliate_id?: string | number | boolean; campaign_id?: string | number | boolean; offer_id?: string | number | boolean; period_end?: string | number | boolean; period_start?: string | number | boolean; status?: string | number | boolean; }): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await affiliateApi.ListConversions(params);

      runInAction(() => {
        this.items = response.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Reject conversion
   */
  async RejectConversion(conversion_id: string, data: RejectConversionRequest): Promise<ConversionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await affiliateApi.RejectConversion(conversion_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const affiliateConversionStore = new AffiliateConversionStore();
